import { Component, HostListener, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ToasterService } from 'angular2-toaster';
import { TagItem } from 'app/components/common/tag/tag.component';
import { PendingChangesAware } from 'app/components/guards/pendingChanges.guard';
import { AuthService } from 'app/services/auth.service';
import { DevicesGroupService } from 'app/services/devicesGroup.service';
import { ModalService } from 'app/services/modal.service';
import { PresentationService } from 'app/services/presentation/presentation.service';
import { Logger } from 'app/shared/logger';
import { LoggerFactory } from 'app/shared/loggerFactory';
import { DeleteGroupModalComponent, DeleteGroupModalResult } from 'app/views/devices/groups/deleteGroupModal.component';
import { DeviceModel } from 'app/views/devices/models/device.model';
import { DevicesGroupModel } from 'app/views/devices/models/devicesGroup.model';
import { ScheduleModel } from 'app/views/devices/models/schedule.model';
import { PresentationModel } from 'app/views/presentations/models/presentation.model';
import { UserModel } from 'app/views/user/models/user.model';

@Component({
    selector: 'psm-edit-devicesGroup',
    templateUrl: 'editDevicesGroup.component.html'
})

export class EditDevicesGroupComponent implements OnInit, PendingChangesAware {

    public id;
    public devices: DeviceModel[];

    public isSaving: boolean = false;
    public isNew: boolean = false;
    public loading: boolean = true;

    public model: DevicesGroupModel;
    public form: FormGroup;
    public name: FormControl;
    public schedule: FormControl;

    private logger: Logger;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toasterService: ToasterService,
        private loggerFactory: LoggerFactory,
        private devicesGroupService: DevicesGroupService,
        private presentationService: PresentationService,
        private modalService: ModalService,
        private authService: AuthService
    ) {
        this.logger = loggerFactory.create('EditDevicesGroupComponent');
    }

    private validateSchedule(scheduleControl: AbstractControl): ValidationErrors | null {
        if (scheduleControl.value && this.presentationService.scheduleOverlap(scheduleControl.value)) {
            return { overlap: 'overlap' };
        } else {
            return null;
        }
    }

    public ngOnChanges() {
        this.name = new FormControl(this.model.name, [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(50)
        ]);
        this.schedule = new FormControl(this.clone(this.model.schedule), [x => this.validateSchedule(x)]);

        this.form = new FormGroup({
            name: this.name,
            schedule: this.schedule
        });
    }

    public async ngOnInit() {

        this.id = this.route.snapshot.params.id;
        this.devices = await this.devicesGroupService.getDevicesOfGroup(this.id);

        if (!this.id) this.isNew = true;

        // Load devices group
        if (this.isNew) {
            this.model = await this.devicesGroupService.createDevicesGroup();
        } else {
            try {
                this.model = await this.devicesGroupService.getDevicesGroup(this.id);
            } catch (e) {
                this.logger.error(`Unable to find devices group`);
                this.toasterService.pop('error', '', 'modules.devices.groups.edit.error-getting-device');
                this.router.navigate(['devices/groups/list']);
            }
        }
        await this.verifyAndSetupDevicesGroupData();
        this.ngOnChanges();
        this.loading = false;
    }

    @HostListener('window:beforeunload')
    public hasPendingChanges() {
        return this.form.dirty;
    }

    private async verifyAndSetupDevicesGroupData() {
        if (!this.model) {
            this.logger.error(`Unable to find devicesGroup.`);
            this.toasterService.pop('error', '', 'modules.devices.groups.edit.error-getting-device');
            this.router.navigate(['devices/groups/list']);
        }

        if (!this.model.schedule) {
            this.model.schedule = { defaultPresentationId: undefined, entries: undefined };
        }

        if (!this.model.schedule.entries) {
            this.model.schedule.entries = [];
        }

        if (!this.model.schedule.defaultPresentationId) {
            this.model.schedule.defaultPresentationId = '';
        }
    }

    public async saveDevice() {
        if (this.form.valid) {
            this.isSaving = true;
            try {

                this.model.name = this.name.value;
                this.model.schedule = this.schedule.value;

                await this.devicesGroupService.saveDevicesGroup(this.model);
                this.model = await this.devicesGroupService.getDevicesGroup(this.model.id);
                await this.verifyAndSetupDevicesGroupData();

                this.ngOnChanges();

                this.toasterService.pop('success', '', 'modules.devices.groups.edit.saving-success');

                // change route if new presentation
                if (this.isNew) {
                    this.isNew = false;
                    this.router.navigate([`devices/groups/edit/${this.model.id}`]);
                }
            } catch (err) {
                this.logger.error(err.message);
                this.toasterService.pop('error', '', 'errors.saving-error');
            }
            this.isSaving = false;
        }
    }

    public async deleteDevicesGroup() {
        try {
            let deleteModalResponse = await this.openDeleteModal();
            if (deleteModalResponse === DeleteGroupModalResult.Cancel) { return; }

            // Override group devices config with group config
            if (deleteModalResponse === DeleteGroupModalResult.OverrideDeviceSettings) {
                this.logger.info('Overriding devices configs.');
                await this.devicesGroupService.overrideDevicesSetting(this.model.id);
            }

            // Delete group
            await this.devicesGroupService.deleteDevicesGroup(this.model.id);

            this.toasterService.pop('success', '', 'modules.devices.groups.edit.delete-success');
            this.router.navigate(['/devices/groups/list']);
        } catch (err) {
            this.logger.error('An error occured while deleting the devices group.', err.message);
            this.toasterService.pop('error', '', 'errors.delete-error');
        }
    }

    private async openDeleteModal() {
        // Get the list of device link to this group to warn user of there unlinking and create tags
        let devicesOfGroup = await this.devicesGroupService.getDevicesOfGroup(this.model.id);
        let listDeviceTags: TagItem[] = [];
        for (let device of devicesOfGroup) {
            let tag = await new TagItem('', device.name, 'default');
            listDeviceTags.push(tag);
        }
        return await this.modalService.open<DeleteGroupModalComponent, DeleteGroupModalResult>(DeleteGroupModalComponent, {
            title: 'modules.devices.groups.edit.delete-modal.title',
            content: 'modules.devices.groups.edit.delete-modal.content',
            deviceTags: listDeviceTags
        });
    }

    private clone(obj: object) {
        return Object.assign({}, JSON.parse(JSON.stringify(obj)));
    }

    public async redirectToGroupDevicesList() {
        if (this.devices.length > 0) {
            this.router.navigate(['/devices/groups/', this.id, 'list']);
        } else {
            this.toasterService.pop('warning', '', 'modules.devices.groups.list.no-device-in-group');
        }
    }
}


// WEBPACK FOOTER //
// ./src/app/views/devices/groups/editDevicesGroup.component.ts